import { comparer, computed, makeObservable } from 'mobx'

import ILogisticItem from '../../models/ILogisticItem'
import Workflow from '../../models/Workflow'
import { isLogisticWorkflow } from '../../utils/logisticsHelper'
import CompaniesStore from '../domain/Companies.store'
import { IFilters } from '../InitialState'
import BaseLogisticsStore from './BaseLogistics.store'

export default abstract class BaseLogisticsMapStore {
  public constructor(
    protected readonly store: BaseLogisticsStore,
    protected readonly companiesStore: CompaniesStore,
  ) {
    makeObservable(this)
  }

  protected abstract get filter(): IFilters

  @computed
  public get filteredCollection() {
    return this.getFilteredCollectionExcludeFilter()
  }

  public get instancesInPeriodInterval(): ILogisticItem[] {
    return [
      ...this.localLogisticItems,
      ...this.store.sharedLogisticItemsInPeriodInterval,
    ]
  }

  @computed
  private get localLogisticItems(): ILogisticItem[] {
    return this.store.convertFormsToLogistics(this.formsByCreationDate)
  }

  protected get formsByCreationDate(): Workflow[] {
    return this.store.formsByCreationDate
  }

  public getFilteredCollectionExcludeFilter = (
    excludedFilters: Array<string> = [],
  ) => {
    let filteredCollection = this.isSearchFilterActive
      ? this.instancesInPeriodInterval.filter(this.searchFiltering)
      : this.instancesInPeriodInterval

    this.activeFilterTypes.forEach(filterType => {
      const shouldUseFilter = !excludedFilters.includes(filterType)

      if (!shouldUseFilter) {
        return
      }
      const filter = this.filter.fieldsMap[filterType]
      const { appliedFilterOptions } = filter

      filteredCollection = filteredCollection.filter(instance =>
        appliedFilterOptions.includes(instance.id),
      )
    })

    return filteredCollection
  }

  @computed({ equals: comparer.shallow })
  public get filteredPermits() {
    return this.filteredCollection
      .filter(isLogisticWorkflow)
      .filter(logistic => !logistic.isShared)
      .map(logistic => logistic.original)
  }

  @computed({ equals: comparer.shallow })
  public get filteredSharedPermits() {
    return this.filteredCollection
      .filter(logistic => logistic.isShared)
      .map(logistic => this.store.getSharedFormById(logistic.entityId))
  }

  @computed
  protected get activeFilterTypes(): string[] {
    const { fieldsMap = {} } = this.filter
    return Object.keys(fieldsMap).filter(
      filterType => fieldsMap[filterType].isFilterActive,
    )
  }

  protected searchFiltering = (logistic: ILogisticItem) => {
    const key = this.filter.searchKey.toLowerCase()
    const entityValues: string[] = [
      ...logistic.locations.map(l => l.name),
      logistic.name,
      logistic.status,
      ...logistic.companyIds.map(id =>
        this.companiesStore.getCompanyNameById(id),
      ),
    ]
    return entityValues.some(value => value?.toLowerCase().includes(key))
  }

  protected get isSearchFilterActive(): boolean {
    return !!this.filter.searchKey
  }
}
